import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, Field, Panel, RowsSkeleton, Tag } from "@/components/app/kit";
import { PatientPicker } from "@/components/med/shared";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
export const post = (url, body, key) => api.post(url, body, { headers: { "Idempotency-Key": key || newKey() } });

export const useMoney = () => {
  const { me } = useAuth();
  const cur = me?.tenant?.currency || "EUR";
  return (v) => (v == null ? "—" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur }).format(v));
};

const FS = {
  draft: ["Brouillon", "amber"], issued: ["Émise", "sky"], issuing: ["Émission…", "sky"], partially_paid: ["Partiellement payée", "indigo"], paid: ["Payée", "green"],
  overdue: ["Échue", "red"], cancelled: ["Annulée", "gray"], valid: ["Valide", "green"], open: ["Ouverte", "green"], closed: ["Clôturée", "gray"],
  submitted: ["Soumise", "sky"], approved: ["Validée", "green"], rejected: ["Rejetée", "red"], ordered: ["Commandée", "indigo"], pending: ["En attente", "amber"],
  partially_received: ["Réception partielle", "indigo"], received: ["Reçue", "green"], posted: ["Validée", "green"], validated: ["Validé", "green"],
  closed_with_rejections: ["Soldé avec rejets", "amber"], requested: ["Demandée", "amber"], expired: ["Expirée", "gray"], paying: ["Paiement…", "sky"],
  partial: ["Partiel", "indigo"], paid_with_rejection: ["Payé avec rejet", "amber"], credit_note: ["Avoir", "indigo"],
};
export const FinStatus = ({ s }) => <Tag tone={FS[s]?.[1] || "gray"} data-testid={`fin-status-${s}`}>{FS[s]?.[0] || s}</Tag>;

const useOptions = (f) => {
  const [opts, setOpts] = useState(f.options || []);
  useEffect(() => {
    if (f.source) api.get(f.source).then((r) => setOpts((Array.isArray(r.data) ? r.data : r.data[f.sourceKey]).map((x) => [x[f.valueKey || "id"], f.optLabel ? f.optLabel(x) : x.name || x.label]))).catch(() => {});
  }, [f.source]); // eslint-disable-line react-hooks/exhaustive-deps
  const staticKey = f.source ? null : JSON.stringify(f.options || []);
  useEffect(() => {
    if (!f.source) setOpts(f.options || []);
  }, [staticKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return opts;
};

const NONE = "__none__";
export const SelectField = ({ f, value, onChange, disabled, testid }) => {
  const opts = useOptions(f);
  return (
    <Select value={value == null || value === "" ? (f.optional ? NONE : undefined) : String(value)} onValueChange={(v) => onChange(v === NONE ? null : v)} disabled={disabled}>
      <SelectTrigger className="bg-white h-9" data-testid={testid}><SelectValue placeholder={f.placeholder || "Choisir"} /></SelectTrigger>
      <SelectContent>{f.optional && <SelectItem value={NONE}>—</SelectItem>}{opts.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
};

export const Input1 = ({ f, value, onChange, disabled, testid }) => {
  const t = f.type || "text";
  if (t === "select") return <SelectField f={f} value={value} onChange={onChange} disabled={disabled} testid={testid} />;
  if (t === "bool") return <div className="h-9 flex items-center"><Switch checked={!!value} onCheckedChange={onChange} disabled={disabled} data-testid={testid} /></div>;
  if (t === "textarea") return <Textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid} />;
  if (t === "patient") return <PatientPicker value={value} onChange={onChange} testid={testid} />;
  if (t === "list") return <Input value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} disabled={disabled} className="bg-white h-9" data-testid={testid} placeholder="valeurs séparées par des virgules" />;
  if (t === "custom") return f.render(value, onChange);
  return <Input type={t} step="any" value={value ?? ""} disabled={disabled} className="bg-white h-9" data-testid={testid}
    onChange={(e) => onChange(t === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value || (f.nullable ? null : ""))} />;
};

export const FormFields = ({ fields, value, onChange, prefix = "f" }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    {fields.filter((f) => !f.hidden?.(value)).map((f) => (
      <Field key={f.k} label={f.label} required={f.required} hint={f.hint} className={f.wide ? "sm:col-span-2" : ""}>
        <Input1 f={f} value={value[f.k]} onChange={(v) => onChange({ ...value, [f.k]: v })} disabled={f.readOnly} testid={`${prefix}-${f.k}`} />
      </Field>
    ))}
  </div>
);

export const FormDialog = ({ title, fields, initial, onSubmit, onClose, submitLabel = "Enregistrer", testid = "form-dialog", wide, children }) => {
  const [f, setF] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  const [key] = useState(newKey);
  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(f, key);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${wide ? "max-w-5xl" : "max-w-2xl"} w-[calc(100vw-1rem)] sm:w-full max-h-[90vh] overflow-y-auto [&>*]:min-w-0`} data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <FormFields fields={fields} value={f} onChange={setF} prefix={testid} />
        {children && children(f, setF)}
        <DialogFooter><Button onClick={submit} disabled={busy} data-testid={`${testid}-submit`}>{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{submitLabel}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const LinesEditor = ({ cols, value, onChange, blank, testid = "lines", readOnly }) => {
  const set = (i, k, v) => onChange(value.map((x, n) => (n === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2 min-w-0">
      <div className="overflow-x-auto border border-slate-200 rounded-lg"><Table><TableHeader><TableRow>{cols.map((c) => <TableHead key={c.k} className="text-xs">{c.label}</TableHead>)}<TableHead /></TableRow></TableHeader>
        <TableBody>{value.map((x, i) => (
          <TableRow key={i}>{cols.map((c) => <TableCell key={c.k} className="p-1 min-w-[110px]">{c.show && c.show(x) !== undefined ? c.show(x) : <Input1 f={c} value={x[c.k]} onChange={(v) => set(i, c.k, v)} disabled={readOnly} testid={`${testid}-${i}-${c.k}`} />}</TableCell>)}
            <TableCell className="p-1">{!readOnly && <Button size="sm" variant="ghost" onClick={() => onChange(value.filter((_, n) => n !== i))} data-testid={`${testid}-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>}</TableCell></TableRow>))}</TableBody></Table></div>
      {!readOnly && <Button size="sm" variant="outline" onClick={() => onChange([...value, blank()])} data-testid={`${testid}-add`}><Plus className="h-4 w-4 mr-1" />Ajouter une ligne</Button>}
    </div>
  );
};

export const DataTable = ({ columns, rows, onRow, testid = "table", empty = "Aucun élément" }) => (
  <Panel className="overflow-hidden" testid={testid}><div className="-m-5 overflow-x-auto">
    {!rows ? <RowsSkeleton /> : !rows.length ? <EmptyState title={empty} /> : (
      <Table><TableHeader><TableRow>{columns.map((c) => <TableHead key={c.k} className={c.right ? "text-right" : ""}>{c.label}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows.map((r, i) => (
          <TableRow key={r.id || i} className={onRow ? "cursor-pointer" : ""} onClick={() => onRow && onRow(r)} data-testid={`${testid}-row-${r.id || i}`}>
            {columns.map((c) => <TableCell key={c.k} className={`${c.right ? "text-right font-mono text-sm" : "text-sm"} ${c.className || ""}`}>{c.render ? c.render(r) : r[c.k] ?? "—"}</TableCell>)}
          </TableRow>))}</TableBody></Table>)}
  </div></Panel>
);

export const CrudTab = ({ path, columns, fields, canWrite, label, testid, defaults = {} }) => {
  const { data, reload } = useFetch(path);
  const [edit, setEdit] = useState(null);
  const save = async (f, key) => {
    const body = Object.fromEntries(fields.map((x) => [x.k, f[x.k] === undefined ? null : f[x.k]]).filter(([, v]) => v !== null));
    if (f.id) await api.put(`${path}/${f.id}`, { ...body, version: f.version });
    else await post(path, body, key);
    toast.success(`${label} enregistré(e)`);
    reload();
  };
  const all = [...fields, { k: "active", label: "Actif", type: "bool" }];
  return (
    <div className="space-y-4">
      {canWrite && <div className="flex justify-end"><Button onClick={() => setEdit({ active: true, ...defaults })} data-testid={`${testid}-new`}><Plus className="h-4 w-4 mr-2" />Ajouter</Button></div>}
      <DataTable testid={testid} rows={data} onRow={canWrite ? setEdit : null}
        columns={[...columns, { k: "active", label: "Statut", render: (r) => <Tag tone={r.active ? "green" : "gray"}>{r.active ? "Actif" : "Inactif"}</Tag> }]} />
      {edit && <FormDialog title={`${edit.id ? "Modifier" : "Ajouter"} — ${label}`} fields={all} initial={edit} onSubmit={save} onClose={() => setEdit(null)} testid={`${testid}-dialog`} />}
    </div>
  );
};

export const PdfLink = ({ url, testid, label = "PDF" }) => {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const r = await api.get(url, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) {
      toast.error("Export PDF impossible");
    } finally {
      setBusy(false);
    }
  };
  return <Button size="sm" variant="outline" onClick={open} disabled={busy} data-testid={testid}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}<span className="ml-2">{label}</span></Button>;
};

export const usePayFields = () => {
  const { hasModule } = useAuth();
  const [methods, setMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    api.get("/t/finance/settings").then((r) => setMethods(r.data.payment_methods.map((x) => [x.code, x.label]))).catch(() => {});
    if (hasModule("cash")) api.get("/t/cash/balances").then((r) => setAccounts([...r.data.registers.map((x) => [`register:${x.id}`, `Caisse · ${x.name}${x.session ? "" : " (fermée)"}`]), ...r.data.banks.map((x) => [`bank:${x.id}`, `Banque · ${x.name}`])])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const fields = [{ k: "method", label: "Moyen de paiement", type: "select", options: methods, required: true }];
  if (accounts.length) fields.push({ k: "account", label: "Caisse / compte", type: "select", options: accounts, optional: true, hint: "Obligatoire pour les espèces" });
  return fields;
};

export const splitAccount = ({ account, ...rest }) => {
  if (!account) return rest;
  const [account_type, account_id] = account.split(":");
  return { ...rest, account_type, account_id };
};

export const Period = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2 items-end">
    <Field label="Du"><Input type="date" value={value.date_from || ""} onChange={(e) => onChange({ ...value, date_from: e.target.value })} className="bg-white h-9" data-testid="period-from" /></Field>
    <Field label="Au"><Input type="date" value={value.date_to || ""} onChange={(e) => onChange({ ...value, date_to: e.target.value })} className="bg-white h-9" data-testid="period-to" /></Field>
  </div>
);

export const qs = (o) => new URLSearchParams(Object.entries(o).filter(([, v]) => v)).toString();
